import React, { useMemo, useEffect, useState } from 'react';
import { FaCalculator, FaLightbulb, FaBrain, FaBook, FaGraduationCap, FaRulerCombined, FaArrowLeft, FaArrowRight, FaRegClock, FaQuestionCircle, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import Header from './Header';
import Footer from './Footer';

const TestListScreen = ({ onStartTest, scrollToSection, tests = [], isLoading, error }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

   useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

   const backgroundIcons = useMemo(() => {
    const iconComponents = [
      FaBook, FaGraduationCap, FaCalculator, FaRulerCombined
    ];
    
    const numberOfIcons = isMobile ? 12 : 20;
    
    return Array.from({ length: numberOfIcons }, (_, index) => {
      const RandomIcon = iconComponents[Math.floor(Math.random() * iconComponents.length)];
      const colors = ['blue-400', 'purple-400', 'green-400', 'yellow-500', 'pink-400', 'indigo-400', 'teal-400'];
      const color = colors[Math.floor(Math.random() * colors.length)];
      
      return {
        id: index,
        Icon: RandomIcon,
        color,
        size: isMobile ? Math.random() * 15 + 15 : Math.random() * 25 + 25,
        left: Math.random() * 100,
        top: Math.random() * 100,
        rotation: Math.random() * 360,
        opacity: Math.random() * 0.08 + 0.02,
        animationDelay: Math.random() * 5
      };
    });
  }, [isMobile]);

   const getTestIcon = (test) => {
    const title = test.title || '';
    if (title.includes('رياضيات') || title.includes('حساب')) {
      return <FaCalculator className="text-2xl text-white" />;
    } else if (title.includes('فيزياء') || title.includes('علم')) {
      return <FaLightbulb className="text-2xl text-white" />;
    } else if (title.includes('ذكاء') || title.includes('منطق')) {
      return <FaBrain className="text-2xl text-white" />;
    } else if (title.includes('هندسة')) {
      return <FaRulerCombined className="text-2xl text-white" />;
    } else {
      return <FaBook className="text-2xl text-white" />;
    }
  };

   const getTestBgColor = (test) => {
    const title = test.title || '';
    if (title.includes('رياضيات') || title.includes('حساب')) {
      return 'bg-gradient-to-br from-blue-500 to-indigo-600';
    } else if (title.includes('فيزياء') || title.includes('علم')) {
      return 'bg-gradient-to-br from-yellow-400 to-orange-500';
    } else if (title.includes('ذكاء') || title.includes('منطق')) {
      return 'bg-gradient-to-br from-purple-500 to-pink-500';
    } else if (title.includes('هندسة')) {
      return 'bg-gradient-to-br from-green-400 to-teal-500';
    } else {
      return 'bg-gradient-to-br from-red-500 to-rose-600';
    }
  };

   const formattedTests = useMemo(() => {
    return tests.map(test => ({
      id: test.id,
      title: test.title,
      description: test.description,
      className: test.className,
      icon: getTestIcon(test),
      bgColor: getTestBgColor(test),
      levels: test.levels || [],
      allQuestions: test.allQuestions || [],
      hearts: test.hearts || { total: 3, remaining: 3 },
      hints: test.hints || { total: 2, remaining: 2 },
      statistics: test.statistics || { 
        totalQuestions: test.allQuestions?.length || 0,
        totalPoints: test.allQuestions?.length || 0,
        averagePointsPerQuestion: 1
      },
       totalLevels: test.totalLevels || (test.levels?.length || 0),
      status: test.status || 'متاح',
      progress: test.progress
    }));
  }, [tests]);

   const getVisibleTests = () => {
    const testsPerPage = isMobile ? 1 : 3;
    const startIndex = currentSlide * testsPerPage;
    return formattedTests.slice(startIndex, startIndex + testsPerPage);
  };

  const handleNextSlide = () => {
    const maxSlide = Math.ceil(formattedTests.length / (isMobile ? 1 : 3)) - 1;
    setCurrentSlide((prev) => (prev >= maxSlide ? 0 : prev + 1));
  };

  const handlePrevSlide = () => {
    const maxSlide = Math.ceil(formattedTests.length / (isMobile ? 1 : 3)) - 1;
    setCurrentSlide((prev) => (prev <= 0 ? maxSlide : prev - 1));
  };

   const getTotalSlides = () => {
    return Math.ceil(formattedTests.length / (isMobile ? 1 : 3));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">جاري تحميل الاختبارات...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaExclamationTriangle className="text-4xl text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">حدث خطأ في جلب البيانات</p>
          <p className="text-sm text-gray-500 mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 overflow-x-hidden" dir="rtl">
      <Header scrollToSection={scrollToSection} />
      
      <div className="relative py-8 sm:py-12 lg:py-16 bg-gray-50 overflow-hidden">
         {backgroundIcons.map((icon) => (
          <icon.Icon
            key={icon.id}
            className={`absolute text-${icon.color} floating`}
            style={{
              left: `${icon.left}%`,
              top: `${icon.top}%`,
              fontSize: `${icon.size}px`,
              transform: `rotate(${icon.rotation}deg)`,
              opacity: icon.opacity,
              animationDelay: `${icon.animationDelay}s`
            }}
          />
        ))}

        <div className="relative px-4 mx-auto max-w-7xl">
          <div className="text-center mb-8">
            <div className="inline-block px-4 py-1 mb-3 bg-indigo-100 rounded-full">
              <h3 className="text-xs font-medium text-purple-900">منصة نافس برو</h3>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">اختر اختباراً <span className="text-purple-900">لبدء رحلة التعلم</span></h1>
            <p className="text-sm sm:text-md text-gray-600 max-w-2xl mx-auto">اكتشف مستواك وطوّر مهاراتك مع مجموعة متنوعة من الاختبارات</p>
          </div>

           <div className="relative mb-8">
            <div className="flex items-center justify-center">
               <button 
                onClick={handlePrevSlide} 
                className="hidden md:block absolute right-0 md:-right-10 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-all duration-300"
                aria-label="السابق"
                disabled={formattedTests.length === 0}
              >
                <FaArrowRight className="text-gray-700 text-lg" />
              </button>
              
               <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-4xl mx-auto py-2 transition-all duration-500">
                {getVisibleTests().map((test) => (
                  <div 
                    key={test.id} 
                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                  >
                    <div className={`h-2 ${test.bgColor}`}></div>
                    <div className="p-4">
                      <div className="flex items-center mb-3">
                        <div className={`w-10 h-10 ${test.bgColor} rounded-xl flex items-center justify-center shadow-md`}>
                          {test.icon}
                        </div>
                        <div className="mr-3">
                          <h3 className="text-lg font-bold text-gray-900">{test.title}</h3>
                          <div className="flex items-center text-xs text-gray-500 mt-1">
                            <FaQuestionCircle className="ml-1" />
                            <span>{test.statistics.totalQuestions} سؤال</span>
                            <span className="mx-1">•</span>
                            <FaRegClock className="ml-1" />
                            <span>{Math.ceil(test.statistics.totalQuestions * 1.5)} دقيقة</span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-gray-600 text-sm mb-4 h-10 line-clamp-2">{test.description}</p>
                      
                      <div className="flex justify-between items-center mb-4 text-xs">
                        <div className="flex items-center text-red-500">
                          <span className="ml-1">قلوب: {test.hearts.remaining}/{test.hearts.total}</span>
                        </div>
                        <div className="flex items-center text-yellow-500">
                          <span className="ml-1">تلميحات: {test.hints.remaining}/{test.hints.total}</span>
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        {test.levels.map((level, index) => (
                          <div key={index} className="flex items-center text-xs">
                            <div className="w-1.5 h-1.5 bg-gray-300 rounded-full ml-1.5"></div>
                            <span className="text-gray-700">{level.levelTitle || `المستوى ${level.levelNumber}`}</span>
                            <span className="mr-auto text-gray-500 text-xs">{level.numberOfQuestions} سؤال</span>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => onStartTest(test)}
                        className={`w-full ${test.bgColor} text-white font-semibold py-2.5 px-4 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg text-sm`}
                      >
                        ابدأ الاختبار
                      </button>
                    </div>
                  </div>
                ))}
                
                {formattedTests.length === 0 && (
                  <div className="col-span-3 text-center py-8">
                    <FaExclamationTriangle className="text-4xl text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">لا توجد اختبارات متاحة حالياً</p>
                  </div>
                )}
              </div>
              
               <button 
                onClick={handleNextSlide} 
                className="hidden md:block absolute left-0 md:-left-10 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-all duration-300"
                aria-label="التالي"
                disabled={formattedTests.length === 0}
              >
                <FaArrowLeft className="text-gray-700 text-lg" />
              </button>
            </div>
            
             {formattedTests.length > 0 && (
              <div className="flex justify-center space-x-1.5 space-x-reverse mt-6">
                {Array.from({ length: getTotalSlides() }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentSlide(i)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      i === currentSlide 
                        ? 'bg-indigo-600 w-6' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`انتقل إلى الشريحة ${i + 1}`}
                  />
                ))}
              </div>
            )}

             {isMobile && formattedTests.length > 0 && (
              <div className="flex justify-center space-x-4 space-x-reverse mt-4">
                <button 
                  onClick={handlePrevSlide} 
                  className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-all duration-300 border border-gray-200"
                  aria-label="السابق"
                >
                  <FaArrowRight className="text-gray-700 text-base" />
                </button>
                <button 
                  onClick={handleNextSlide} 
                  className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-all duration-300 border border-gray-200"
                  aria-label="التالي"
                >
                  <FaArrowLeft className="text-gray-700 text-base" />
                </button>
              </div>
            )}
          </div>
          
           {formattedTests.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white p-4 rounded-xl shadow-sm border-t-2 border-indigo-500">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center ml-3">
                    <FaQuestionCircle className="text-indigo-600 text-sm" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">+{formattedTests.reduce((total, test) => total + test.statistics.totalQuestions, 0)}</h3>
                    <p className="text-gray-600 text-xs">سؤال متنوع</p>
                  </div>
                </div>
                <p className="text-gray-500 text-xs">أسئلة مصممة بعناية لتقييم المهارات المختلفة</p>
              </div>
              
              <div className="bg-white p-4 rounded-xl shadow-sm border-t-2 border-purple-500">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center ml-3">
                    <FaGraduationCap className="text-purple-600 text-sm" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{formattedTests.length}</h3>
                    <p className="text-gray-600 text-xs">اختبار متاح</p>
                  </div>
                </div>
                <p className="text-gray-500 text-xs">اختبارات في مجالات متعددة تناسب جميع الاهتمامات</p>
              </div>
              
              <div className="bg-white p-4 rounded-xl shadow-sm border-t-2 border-green-500">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center ml-3">
                    <FaBook className="text-green-600 text-sm" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{Math.max(...formattedTests.map(test => test.levels.length))}</h3>
                    <p className="text-gray-600 text-xs">مستويات صعوبة</p>
                  </div>
                </div>
                <p className="text-gray-500 text-xs">اختبارات متدرجة الصعوبة لجميع المستويات</p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <Footer scrollToSection={scrollToSection} />

      <style jsx>{`
        .floating {
          animation: float 8s ease-in-out infinite;
          pointer-events: none;
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(var(--rotation, 0deg));
          }
          50% {
            transform: translateY(-15px) rotate(var(--rotation, 0deg));
          }
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* تحسين التوافق مع الهواتف المحمولة */
        @media (max-width: 767px) {
          .floating {
            animation: float 10s ease-in-out infinite;
          }
        }
      `}</style>
    </div>
  );
};

export default TestListScreen;